import { getStoredToken } from './authService';
import { API_BASE_URL } from '../config/api';

// ========================================
// Types for Music Sheet Service
// ========================================

export interface MusicSheet {
  sheet_id?: string;
  sheet_name: string;
  description?: string;
  abc_notation: string;
  metadata?: {
    genre?: string;
    tempo?: string;
    key?: string;
    mood?: string;
  };
  generation_prompt?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SheetSummary {
  sheet_id: string;
  sheet_name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface GenerateOptions {
  textPrompt?: string;
  genre?: string;
  tempo?: string;
  key?: string;
  mood?: string;
}

// Helper function to get auth token
const getAuthToken = async (): Promise<string> => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// ========================================
// Music Sheet Service Class
// ========================================

export class MusicSheetService {
  /**
   * Generate a new music sheet using AI
   * @param options Generation options (text prompt and/or structured inputs)
   */
  public static async generateMusicSheet(options: GenerateOptions): Promise<MusicSheet> {
    try {
      console.log('🎵 [MusicSheetService] Generating new music sheet...');

      const result = await apiCall('/music-sheets/generate', {
        method: 'POST',
        body: JSON.stringify(options)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate music sheet');
      }

      console.log('🎵 [MusicSheetService] Music sheet generated successfully:', {
        sheet_name: result.data?.sheet_name,
        notationLength: result.data?.abc_notation?.length
      });

      return result.data;
    } catch (error) {
      console.error('🎵 [MusicSheetService] Error generating music sheet:', error);
      
      // Handle authentication errors
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to generate music sheets.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Save music sheet to database
   */
  public static async saveMusicSheet(sheetData: {
    sheet_name: string;
    description?: string;
    abc_notation: string;
    metadata?: MusicSheet['metadata'];
    generation_prompt?: string;
  }): Promise<MusicSheet> {
    try {
      const result = await apiCall('/music-sheets', {
        method: 'POST',
        body: JSON.stringify(sheetData)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to save music sheet');
      }

      return result.data;
    } catch (error) {
      console.error('🎵 [MusicSheetService] Error saving music sheet:', error);
      throw error;
    }
  }

  /**
   * Get past music sheets for user
   */
  public static async getMusicSheets(limit: number = 50, offset: number = 0): Promise<{
    sheets: SheetSummary[];
    total: number;
  }> {
    try {
      const result = await apiCall(`/music-sheets?limit=${limit}&offset=${offset}&sortBy=created_at&order=desc`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch music sheets');
      }

      return {
        sheets: result.data.sheets || [],
        total: result.data.total || 0
      };
    } catch (error) {
      console.error('🎵 [MusicSheetService] Error fetching music sheets:', error);
      throw error;
    }
  }

  /**
   * Get specific music sheet by ID
   */
  public static async getSheetById(sheetId: string): Promise<MusicSheet> {
    try {
      const result = await apiCall(`/music-sheets/${sheetId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch music sheet');
      }

      return result.data;
    } catch (error) {
      console.error('🎵 [MusicSheetService] Error fetching music sheet:', error);
      throw error;
    }
  }

  /**
   * Update music sheet name, description, and/or ABC notation
   */
  public static async updateSheet(sheetId: string, updates: {
    sheet_name?: string;
    description?: string;
    abc_notation?: string;
  }): Promise<MusicSheet> {
    try {
      const result = await apiCall(`/music-sheets/${sheetId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update music sheet');
      }

      return result.data;
    } catch (error) {
      console.error('🎵 [MusicSheetService] Error updating music sheet:', error);
      throw error;
    }
  }

  /**
   * Delete music sheet
   */
  public static async deleteSheet(sheetId: string): Promise<void> {
    try {
      const result = await apiCall(`/music-sheets/${sheetId}`, {
        method: 'DELETE'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete music sheet');
      }
    } catch (error) {
      console.error('🎵 [MusicSheetService] Error deleting music sheet:', error);
      throw error;
    }
  }
}

